import type { LineSeriesOption } from 'echarts';
import type { RetirementChartData, RetirementChartConfig, RetirementSeriesColor } from './types';

type Point = [number, number];

// Map an estimate array onto [age, value] points so the numeric x-axis reads as an age.
const toPoints = (values: number[], age: number): Point[] => values.map((v, i) => [age + i, v]);

// The three estimate lines share a shape; only color, dash and paint order differ.
// z ordering (high < expected < low) mirrors the AMCharts zIndex (10/50/100) so the solid
// opacity-1 area fills stack into visible bands: light (high) behind medium (expected) behind
// the low fill, which paints last on top.
const buildSeries = (
  name: string,
  points: Point[],
  color: RetirementSeriesColor,
  dashed: boolean,
  z: number,
  animated: boolean,
): LineSeriesOption => ({
  name,
  type: 'line',
  data: points,
  smooth: true, // matches AMCharts tensionX = 1
  symbol: 'circle',
  showSymbol: false,
  z,
  lineStyle: { color: color.stroke, width: 2, ...(dashed ? { type: 'dashed' as const } : {}) },
  itemStyle: { color: color.stroke },
  areaStyle: { color: color.fill, opacity: 1 },
  // No hover emphasis: the bands stay fully painted at all times. The tooltip alone reflects the
  // hovered band (see useHoveredRetirementBand); we don't dim the other bands.
  emphasis: { disabled: true },
  ...(animated ? {} : { animation: false }),
});

// Single source of truth for series names, shared with the legend so its swatches line up.
export const RETIREMENT_SERIES_NAMES = {
  high: 'High Estimate',
  expected: 'Expected Net Investments',
  low: 'Low Estimate',
} as const;

export const buildRetirementSeries = (
  data: RetirementChartData,
  values: { high: number[]; expected: number[]; low: number[] },
  config: RetirementChartConfig,
): LineSeriesOption[] => {
  const { seriesColors, animated = true } = config;
  const { age } = data;

  return [
    buildSeries(RETIREMENT_SERIES_NAMES.high, toPoints(values.high, age), seriesColors.high, true, 10, animated),
    buildSeries(
      RETIREMENT_SERIES_NAMES.expected,
      toPoints(values.expected, age),
      seriesColors.expected,
      false,
      50,
      animated,
    ),
    buildSeries(RETIREMENT_SERIES_NAMES.low, toPoints(values.low, age), seriesColors.low, true, 100, animated),
  ];
};
